import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router';

const HomePage = lazy(() => import('./pages/HomePage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));
const RunGamePage = lazy(() => import('./pages/run-game/RunGamePage'));
const PostPage = lazy(() => import('./pages/post/PostPage'));
const BoardPage = lazy(() => import('./pages/board/BoardPage'));
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage'));
const ExternalRedirect = lazy(() => import('../../shared/navigation/ExternalRedirect'));
const ExternalParamRedirect = lazy(() => import('../../shared/navigation/ExternalParamRedirect'));
const RegisterPage = lazy(() => import('./pages/register/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password/ForgotPasswordPage'));
const ActivitiesPage = lazy(() => import('./pages/activities/ActivitiesPage'));
const ActivityPage = lazy(() => import('./pages/activities/ActivityPage'));
const ResourcesPage = lazy(() => import('./pages/resources/ResourcesPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

/**
 * Rutas de rolsensorial.com.
 *
 * Este archivo es la frontera del sitio: para extraerlo como micro-front
 * basta con llevarse `src/sites/role/` junto con este archivo.
 *
 * **Las rutas se escriben en inglés.** El sitio Jekyll las tenía en español
 * (`/contacto`, `/perfil`, `/notificaciones`) y aquí se renombraron todas de
 * una vez: la interfaz sigue en español neutro, pero la URL es parte del
 * código y queda lista para internacionalizar sin arrastrar dos idiomas
 * mezclados. `/panel` se queda como está porque la palabra existe igual en
 * los dos idiomas. `/posada` es otra excepción deliberada: es el nombre propio
 * de esa experiencia.
 */
export const roleRoutes: RouteObject[] = [
  { index: true, element: <HomePage /> },
  // El panel personal, mis actividades, reportar un error y las
  // notificaciones se mudaron a panel.rolsensorial.com. `panel/activities/*`
  // sí vale la pena preservar el identificador (a diferencia del código de
  // una mesa efímera): `activityId` es estable.
  { path: 'panel', element: <ExternalRedirect href="https://panel.rolsensorial.com/" /> },
  { path: 'panel/report-bug', element: <ExternalRedirect href="https://panel.rolsensorial.com/report-bug" /> },
  { path: 'panel/my-report', element: <ExternalRedirect href="https://panel.rolsensorial.com/my-report" /> },
  { path: 'panel/activities', element: <ExternalRedirect href="https://panel.rolsensorial.com/activities" /> },
  { path: 'panel/activities/new', element: <ExternalRedirect href="https://panel.rolsensorial.com/activities/new" /> },
  {
    path: 'panel/activities/:activityId/edit',
    element: <ExternalParamRedirect base="https://panel.rolsensorial.com/activities" param="activityId" suffix="/edit" />,
  },
  {
    path: 'panel/activities/:activityId',
    element: <ExternalParamRedirect base="https://panel.rolsensorial.com/activities" param="activityId" />,
  },
  { path: 'notifications', element: <ExternalRedirect href="https://panel.rolsensorial.com/notifications" /> },
  { path: 'users/:profileRef', element: <ProfilePage /> },
  { path: 'settings', element: <SettingsPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'dirige-una-partida', element: <RunGamePage /> },
  // La ruta se pensó primero en inglés (`run-a-game`) y se cambió antes de
  // haber llegado a difundirse; esta redirección es solo por si algún
  // enlace guardado quedó apuntando ahí.
  { path: 'run-a-game', element: <Navigate to="/dirige-una-partida" replace /> },
  { path: 'post/:boardId/:slug', element: <PostPage /> },
  { path: 'board/:boardId', element: <BoardPage /> },
  { path: 'messages', element: <MessagesPage /> },
  { path: 'messages/:conversationId', element: <MessagesPage /> },
  // La Taberna y La Posada se mudaron a zona.rolsensorial.com: todo lo que
  // apunte a estas rutas se manda para allá, no se preserva el identificador
  // de la mesa ni la clave de acceso (igual que ya hacía `/chat`).
  { path: 'chat', element: <ExternalRedirect href="https://zona.rolsensorial.com/" /> },
  { path: 'taberna', element: <ExternalRedirect href="https://zona.rolsensorial.com/taberna" /> },
  { path: 'taberna/juegos', element: <ExternalRedirect href="https://zona.rolsensorial.com/taberna/juegos" /> },
  { path: 'taberna/:chatId', element: <ExternalRedirect href="https://zona.rolsensorial.com/taberna" /> },
  { path: 'posada', element: <ExternalRedirect href="https://zona.rolsensorial.com/posada" /> },
  { path: 'posada/:accessKey', element: <ExternalRedirect href="https://zona.rolsensorial.com/posada" /> },
  // Las aplicaciones (dados, palabras, música…) y los playgrounds se
  // mudaron a app.rolsensorial.com. Acá sí vale la pena preservar el
  // identificador: a diferencia del código de una mesa, `appId`,
  // `playgroundId` y el token de un enlace compartido son estables.
  { path: 'apps', element: <ExternalRedirect href="https://app.rolsensorial.com/apps" /> },
  { path: 'app/:appId', element: <ExternalParamRedirect base="https://app.rolsensorial.com/app" param="appId" /> },
  { path: 'playgrounds', element: <ExternalRedirect href="https://app.rolsensorial.com/playgrounds" /> },
  // `shared` va antes que `:playgroundId` porque si no, un enlace compartido
  // se leería como el identificador de un playground llamado "shared".
  {
    path: 'playgrounds/shared/:shareToken',
    element: <ExternalParamRedirect base="https://app.rolsensorial.com/playgrounds/shared" param="shareToken" />,
  },
  {
    path: 'playgrounds/:playgroundId/settings',
    element: <ExternalParamRedirect base="https://app.rolsensorial.com/playgrounds" param="playgroundId" suffix="/settings" />,
  },
  {
    path: 'playgrounds/:playgroundId',
    element: <ExternalParamRedirect base="https://app.rolsensorial.com/playgrounds" param="playgroundId" />,
  },
  { path: 'activities', element: <ActivitiesPage /> },
  { path: 'activities/:activityId', element: <ActivityPage /> },
  { path: 'register', element: <RegisterPage /> },
  { path: 'forgot-password', element: <ForgotPasswordPage /> },
  { path: 'resources', element: <ResourcesPage /> },
];

/**
 * Rutas alcanzables sin sesión. El resto del sitio vive detrás del acceso con
 * Google o con usuario/contraseña; contacto es pública porque también la usa
 * quien todavía no tiene cuenta, igual que en el sitio Jekyll, y `/post/`
 * porque ahí viven las páginas de información general (acerca de, aviso
 * legal, privacidad) que cualquiera debe poder leer antes de registrarse.
 */
export const rolePublicPaths = {
  // `/run-a-game` es solo la redirección a `/dirige-una-partida` (ver arriba):
  // pública también, para que resuelva antes de pedir sesión.
  exact: new Set(['/', '/contact', '/dirige-una-partida', '/run-a-game', '/register', '/forgot-password', '/activities', '/resources']),
  prefixes: ['/post/', '/activities/'],
};
